import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Trash2, Eye, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;

export default function AdminContacts() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleMarkRead = async (messageId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/contacts/${messageId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Es-tu sûr de vouloir supprimer ce message ?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/contacts/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message supprimé');
      setSelectedMessage(null);
      fetchMessages();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-contacts">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-rm-text">Messages de contact</h1>
        <p className="text-rm-text-secondary">Gérez les demandes reçues via le formulaire de contact</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Messages List */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Messages ({messages.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="spinner"></div>
              </div>
            ) : messages.length === 0 ? (
              <p className="text-rm-text-secondary text-center py-10">
                Aucun message pour le moment
              </p>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => {
                      setSelectedMessage(message);
                      if (!message.read) handleMarkRead(message.id);
                    }}
                    className={`p-4 rounded-xl cursor-pointer transition-colors ${
                      selectedMessage?.id === message.id 
                        ? 'bg-rm-primary/10 border-2 border-rm-primary' 
                        : 'bg-slate-50 hover:bg-slate-100'
                    } ${!message.read ? 'border-l-4 border-l-rm-secondary' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-rm-text truncate">{message.name}</p>
                        <p className="text-sm text-rm-text-secondary truncate">{message.email}</p>
                      </div>
                      {!message.read && (
                        <Badge className="bg-rm-secondary text-white flex-shrink-0">Nouveau</Badge>
                      )}
                    </div>
                    <p className="text-sm text-rm-text-secondary mt-2 line-clamp-2">
                      {message.message}
                    </p>
                    <p className="text-xs text-rm-text-secondary mt-2">
                      {formatDate(message.created_at)}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Message Detail */}
        <Card className="h-fit sticky top-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Détail du message
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedMessage ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-rm-text-secondary">Nom</p>
                  <p className="font-semibold text-rm-text">{selectedMessage.name}</p>
                </div>
                <div>
                  <p className="text-sm text-rm-text-secondary">Email</p>
                  <a 
                    href={`mailto:${selectedMessage.email}`}
                    className="font-semibold text-rm-primary hover:underline"
                  >
                    {selectedMessage.email}
                  </a>
                </div>
                {selectedMessage.phone && (
                  <div>
                    <p className="text-sm text-rm-text-secondary">Téléphone</p>
                    <p className="font-semibold text-rm-text">{selectedMessage.phone}</p>
                  </div>
                )}
                {selectedMessage.company && (
                  <div>
                    <p className="text-sm text-rm-text-secondary">Entreprise</p>
                    <p className="font-semibold text-rm-text">{selectedMessage.company}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-rm-text-secondary">Message</p>
                  <p className="text-rm-text whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>
                <div>
                  <p className="text-sm text-rm-text-secondary">Date</p>
                  <p className="text-rm-text">{formatDate(selectedMessage.created_at)}</p>
                </div>
                <div className="flex gap-2 pt-4 border-t">
                  <a 
                    href={`mailto:${selectedMessage.email}`}
                    className="flex-1"
                  >
                    <button className="w-full py-2 px-4 bg-rm-primary text-white rounded-xl hover:bg-rm-primary/90 transition-colors">
                      Répondre
                    </button>
                  </a>
                  <button
                    onClick={() => handleDelete(selectedMessage.id)}
                    className="p-2 bg-red-100 text-red-600 rounded-xl hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-rm-text-secondary text-center py-10">
                Sélectionnez un message pour voir les détails
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
